import type Phaser from "phaser";

/**
 * The emoji surface's sprites (P2.19-T7 and P2.19-T16): a still emoji's and a copy's image with where and how it was
 * last drawn, so a frame that changes nothing touches nothing, and the three things done to one: made hidden, hidden,
 * and drawn at a place in device pixels.
 */

/**
 * One sprite: the image, the frame set on it, and where and how it was last drawn, so a frame that changes nothing
 * touches nothing.
 */
export interface Drawn {
  readonly image: Phaser.GameObjects.Image;
  frame: string;
  deviceLeft: number;
  deviceTop: number;
  scale: number;
  k: number;
  alpha: number;
  shown: boolean;
}

/**
 * A sprite drawn nowhere yet.
 *
 * @param image Its image.
 * @returns The sprite, hidden.
 */
export function drawn(image: Phaser.GameObjects.Image): Drawn {
  return { image, frame: "", deviceLeft: Number.NaN, deviceTop: Number.NaN, scale: 0, k: 0, alpha: 1, shown: false };
}

/**
 * Hides a sprite, if shown.
 *
 * @param sprite The sprite.
 */
export function hide(sprite: Drawn): void {
  if (sprite.shown) {
    sprite.image.setVisible(false);
    sprite.shown = false;
  }
}

/**
 * Draws a sprite at a place in device pixels, k device pixels an art pixel, at a strength, setting only what changed.
 *
 * @param sprite The sprite.
 * @param left Its left in device pixels.
 * @param top Its top in device pixels.
 * @param scale The UI scale.
 * @param k Device pixels an art pixel.
 * @param alpha Its strength.
 */
export function place(sprite: Drawn, left: number, top: number, scale: number, k: number, alpha: number): void {
  if (left !== sprite.deviceLeft || top !== sprite.deviceTop || scale !== sprite.scale) {
    sprite.image.setPosition(left / scale, top / scale);
    sprite.deviceLeft = left;
    sprite.deviceTop = top;
  }
  if (scale !== sprite.scale || k !== sprite.k) {
    sprite.image.setScale(k / scale);
    sprite.scale = scale;
    sprite.k = k;
  }
  if (alpha !== sprite.alpha) {
    sprite.image.setAlpha(alpha);
    sprite.alpha = alpha;
  }
  if (!sprite.shown) {
    sprite.image.setVisible(true);
    sprite.shown = true;
  }
}
